import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useReferrals } from '../queries/useReferralsQueries';
import { copyToClipboard, downloadCSV } from '@/lib';
import { configSettings } from '@/lib/config';
import type { StanFilterValue } from '@/components/core-components/StanFilters/types';
import { getSubscriptionStatusLabel } from '../logic';

export function useReferralsTable() {
    const [searchParams, setSearchParams] = useSearchParams();

    // Initialize state from URL params
    const getInitialFilters = (): StanFilterValue[] => {
        const filtersParam = searchParams.get('filters');
        if (filtersParam) {
            try {
                return JSON.parse(filtersParam);
            } catch {
                return [];
            }
        }
        return [];
    };

    const getInitialPage = (): number => {
        const pageParam = searchParams.get('page');
        return pageParam ? parseInt(pageParam, 10) : 1;
    };

    const getInitialExcludeInactive = (): boolean => {
        const excludeParam = searchParams.get('excludeInactive');
        return excludeParam === 'true';
    };

    const [filterValues, setFilterValues] = useState<StanFilterValue[]>(getInitialFilters);
    const [currentPage, setCurrentPage] = useState(getInitialPage);
    const [excludeInactive, setExcludeInactive] = useState(getInitialExcludeInactive);

    // Fetch referrals data
    const { referrals, metadata, isLoading, refetch } = useReferrals({
        page: currentPage,
        pageSize: 10,
        exclude_inactive: excludeInactive ? 1 : 0,
        filter_array: filterValues,
    });

    // Sync state with URL params
    useEffect(() => {
        const params = new URLSearchParams();

        if (filterValues.length > 0) {
            params.set('filters', JSON.stringify(filterValues));
        }

        if (currentPage > 1) {
            params.set('page', currentPage.toString());
        }

        if (excludeInactive) {
            params.set('excludeInactive', 'true');
        }

        const newSearch = params.toString();
        const currentSearch = searchParams.toString();

        // Only update if changed to avoid unnecessary navigation
        if (newSearch !== currentSearch) {
            setSearchParams(params, { replace: true });
        }
    }, [filterValues, currentPage, excludeInactive, setSearchParams, searchParams]);

    // Handlers
    const handleToggleChange = (checked: boolean | React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = typeof checked === 'boolean' ? checked : checked.target.checked;
        setExcludeInactive(isChecked);
        setCurrentPage(1); // Reset to first page when toggling
    };

    const handleFilterChange = (newFilterValues: StanFilterValue[]) => {
        setFilterValues(newFilterValues);
        setCurrentPage(1); // Reset to first page when filtering
    };

    const handleResetFilters = () => {
        setFilterValues([]);
        setCurrentPage(1);
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleCopyLink = (link: string) => {
        copyToClipboard(link, {
            successMessage: 'Referral link copied to clipboard',
            errorMessage: 'Failed to copy link to clipboard',
        });
    };

    const handleDownloadCSV = () => {
        if (!referrals.length) return;

        const headers = [
            'Username',
            'Full Name',
            'Email',
            'Sign Up Date',
            'Source',
            'Subscription Status',
            'Stan Link',
        ];

        const rows = referrals.map(referral => [
            referral.username,
            referral.fullName,
            referral.email,
            referral.subscriberSince,
            referral.utm_source,
            getSubscriptionStatusLabel(referral.subscriptionStatus),
            `${configSettings.frontDomain}${referral.username}`,
        ]);

        downloadCSV({
            filename: `referrals-${new Date().toISOString().split('T')[0]}`,
            headers,
            rows,
        });
    };

    // Computed values
    const hasFilters = filterValues.length > 0 || excludeInactive;
    const emptyHeader = hasFilters ? 'No Matching Referrals' : 'No Referrals Yet';
    const emptySubheader = hasFilters
        ? 'Try adjusting your filters to see more results.'
        : "You haven't referred any users yet. Share your referral link to start earning!";

    return {
        // State
        filterValues,
        currentPage,
        excludeInactive,

        // Data
        referrals,
        metadata,
        isLoading,

        // Computed
        hasFilters,
        emptyHeader,
        emptySubheader,

        // Handlers
        handleToggleChange,
        handleFilterChange,
        handleResetFilters,
        handlePageChange,
        handleCopyLink,
        handleDownloadCSV,

        // Refetch
        refetch,
    };
}
